import { XMarkIcon } from "@heroicons/react/24/outline";
import { useEffect, useRef, useState } from "react";

import { FileUploadAndParse } from "@fronterahealth/frontera-ui-components";

import {
  WetSignatureCaptureMethodEnums,
  useCreateReportSectionFieldFileMutation,
  useUpdateReportSectionFieldFileUrlMutation,
} from "@api/graphql/types-and-hooks";
import { getFieldImageSignedUrl } from "@components/FieldComponent/utils";
import { ReportFieldDisabledValue } from "@components/ReportField/ReportFieldDisabledValue";
import { ReportFieldInputProps } from "@components/ReportField/types";
import { useUnsavedMarker } from "@components/ReportField/useUnsavedMarker";
import { useReport } from "@providers/ReportProvider";

interface ReportFieldImageProps extends ReportFieldInputProps {
  // Set by ReportFieldWetSignature when this component is reused as the
  // "uploaded" method sub-widget of a WET_SIGNATURE field — threaded
  // through to updateReportSectionFieldFileUrl so the backend wraps the
  // computed S3 key into the WET_SIGNATURE content envelope (task 2.2).
  // Unset for plain IMAGE/ASSESSMENT_IMAGE fields, which keep writing a
  // bare key exactly as before.
  captureMethod?: WetSignatureCaptureMethodEnums;
}

export const ReportFieldImage: React.FC<ReportFieldImageProps> = ({
  onChange,
  field,
  value,
  disabled,
  variant,
  captureMethod,
}) => {
  const { report, reportQuery } = useReport();
  const createFileMutation = useCreateReportSectionFieldFileMutation({ throwOnError: false });
  const updateFileUrlMutation = useUpdateReportSectionFieldFileUrlMutation({ throwOnError: false });
  const [hovered, setHovered] = useState(false);
  const { mark: markUnsaved, clear: clearUnsaved } = useUnsavedMarker(`${field.id}:upload`);

  const imageSignedUrl = getFieldImageSignedUrl(field);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!hovered) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (event.target instanceof Node && ref.current && !ref.current.contains(event.target)) {
        setHovered(false);
      }
    };
    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, [ref, hovered]);

  if (disabled) {
    if (imageSignedUrl) {
      return (
        <div className="flex justify-center items-center rounded-lg border border-dashed border-limestone-200 px-6 transition relative hover:bg-limestone-50">
          <div className="rounded-xl">
            <img
              src={imageSignedUrl}
              alt="Invalid URL"
              className="max-w-full max-h-[32rem] object-contain p-2 rounded-xl"
            />
          </div>
        </div>
      );
    }
    return <ReportFieldDisabledValue />;
  }

  if (imageSignedUrl) {
    return (
      <div className="flex justify-center items-center rounded-lg border border-dashed border-limestone-200 px-6 transition relative hover:bg-limestone-50">
        <div className="rounded-xl">
          <img
            src={imageSignedUrl}
            alt="Invalid URL"
            className="max-w-full max-h-[32rem] object-contain p-2 rounded-xl"
          />
          <XMarkIcon
            onClick={() =>
              onChange("", {
                onSuccess: async () => {
                  await reportQuery.refetch();
                },
              })
            }
            className="h-6 w-6 absolute top-2 right-2 cursor-pointer rounded-md border border-limestone-200 bg-white p-1 text-text-secondary transition-all hover:bg-red-200"
          />
        </div>
      </div>
    );
  }

  return (
    <div onClick={() => setHovered(true)} ref={ref}>
      {!hovered && variant === "ghost" && (
        <div className="flex flex-col gap-4 rounded-lg border border-input px-4 py-2 -my-2 transition-[color,box-shadow,padding,border-color] [&:not(:hover):not(:focus-visible)]:border-transparent [&:not(:hover):not(:focus-visible)]:px-0">
          <ReportFieldDisabledValue />
        </div>
      )}
      {(hovered || variant !== "ghost") && (
        <FileUploadAndParse
          key={JSON.stringify(value)}
          fileKind={field.id}
          existingFiles={[]}
          title=""
          singleFileUpload
          disabled={false}
          supportedFileTypes={["png", "jpg", "gif"]}
          maxSize={"100MB"}
          S3BucketUrlRetriever={async ({ fileName, mimeType, fileSize }) => {
            markUnsaved();
            try {
              const res = await createFileMutation.mutateAsync({
                input: { reportId: report.id, fieldId: field.id, fileSize, mimeType, originalFileName: fileName },
              });
              return { bucket: res?.createReportSectionFieldFile?.fileUrl ?? "", fileId: field.id };
            } catch (error) {
              clearUnsaved();
              throw error;
            }
          }}
          S3BucketUploadCallback={async ({ file, url, mimeType }) => {
            try {
              const response = await fetch(url, { method: "PUT", headers: { "Content-Type": mimeType }, body: file });
              if (!response.ok) {
                throw new Error(`S3 upload failed: ${response.status} ${response.statusText}`);
              }
              await updateFileUrlMutation.mutateAsync({
                input: { reportId: report.id, fieldId: field.id, originalFileName: file.name, captureMethod },
              });
              await reportQuery.refetch();
            } finally {
              clearUnsaved();
            }
          }}
          reportFileUploadCompleteCallback={async () => {
            await reportQuery.refetch();
          }}
          fileParseStatusRetriever={(fileId) => Promise.resolve({ fileId, status: "done" })}
          removeFileCallback={() => {
            return Promise.resolve();
          }}
        />
      )}
    </div>
  );
};
